import React, { useState } from "react";
import {
  InsertToMode,
  InsertTarget,
  MachineCollection,
  PendingKlydo,
} from "../../../../Types";
import DSDialog from "../../../system-design/DSDialog";
import DSDialogTitle from "../../../system-design/DSDialogTitle";
import DSDialogContent from "../../../system-design/DSDialogContent";
import DSDialogActions from "../../../system-design/DSDialogActions";
import DSPrimaryButton from "../../../system-design/DSPrimaryButton";
import DSSecondaryButton from "../../../system-design/DSSecondaryButton";
import DSBox from "../../../system-design/DSBox";
import DSSelect from "../../../system-design/DSSelect";
import DSMenuItem from "../../../system-design/DSMenuItem";
import DSTextField from "../../../system-design/DSTextField";
import DSSwitch from "../../../system-design/DSSwitch";
import DSFormControlLabel from "../../../system-design/DSFormControlLabel";
import DSFormControl from "../../../system-design/DSFormControl";
import DSInputLabel from "../../../system-design/DSInputLabel";
import DSTypography from "../../../system-design/DSTypography";
import KlydosPickDialog from "../../../Gifts/GiftCategories/components/KlydosPickDialog";
import useExploreCategoriesQuery from "../../../ExploreCategories/hooks/useExploreCategoriesQuery";
import { useQueries } from "@tanstack/react-query";
import collectionApiClient from "../../../../api/collection";
import { KlydoType } from "../../../../api/klydo";

interface AddPendingKlydoDialogProps {
  open: boolean;
  onClose: () => void;
  onAdd: (pendingKlydos: PendingKlydo[]) => void;
  existingCollections: MachineCollection[];
  /**
   * False on a regular clock, where the clock's own fetch is sealed-only so a
   * queued draft silently never arrives. There is NO server check on this
   * write — the caller enforces the rule before writing Firestore, and this
   * flag only shapes the dialog (picker restriction + the notice below).
   */
  draftsAllowed: boolean;
  errorMessage?: string | null;
  isSaving?: boolean;
}

const ALL_MODES: InsertToMode[] = [
  "FEED",
  "FAVORITE",
  "COLLECTIONS",
  "EXPLORE",
];

const AddPendingKlydoDialog: React.FC<AddPendingKlydoDialogProps> = ({
  open,
  onClose,
  onAdd,
  existingCollections,
  draftsAllowed,
  errorMessage,
  isSaving,
}) => {
  const [selectedKlydoIds, setSelectedKlydoIds] = useState<string[]>([]);
  const [showKlydoPicker, setShowKlydoPicker] = useState(false);
  const [checkedModes, setCheckedModes] = useState<Set<InsertToMode>>(
    new Set<InsertToMode>(["FEED"]),
  );
  const [collectionTarget, setCollectionTarget] = useState<string>("");
  const [exploreCategoryTarget, setExploreCategoryTarget] =
    useState<string>("");
  const { data: exploreCategories } = useExploreCategoriesQuery();
  const collectionQueries = useQueries({
    queries: existingCollections.map((col) => ({
      queryKey: ["collection", col.id],
      queryFn: () => collectionApiClient.getCollectionById(col.id),
      enabled: checkedModes.has("COLLECTIONS"),
      staleTime: Infinity,
      refetchOnWindowFocus: false,
    })),
  });
  const collectionNameMap = new Map(
    existingCollections.map((col, i) => [
      col.id,
      collectionQueries[i]?.data?.name ?? col.id,
    ]),
  );
  const [ttlEnabled, setTtlEnabled] = useState(false);
  const [ttl, setTtl] = useState<number>(60);
  const [scheduleEnabled, setScheduleEnabled] = useState(false);
  const [scheduledTime, setScheduledTime] = useState("");
  const [playNow, setPlayNow] = useState(false);
  const [forceDownload, setForceDownload] = useState(false);

  const toggleMode = (mode: InsertToMode) => {
    setCheckedModes((prev) => {
      const next = new Set(prev);
      if (next.has(mode)) {
        next.delete(mode);
      } else {
        next.add(mode);
      }
      return next;
    });
  };

  const handleSave = () => {
    if (selectedKlydoIds.length === 0) return;

    const insertTo: InsertTarget[] = ALL_MODES.filter((m) =>
      checkedModes.has(m),
    ).map((mode) => ({
      mode,
      target:
        mode === "COLLECTIONS"
          ? collectionTarget
          : mode === "EXPLORE"
            ? exploreCategoryTarget
            : null,
    }));

    const pendingKlydos: PendingKlydo[] = selectedKlydoIds.map((klydoId) => ({
      id: klydoId,
      insertTo,
      ttl: ttlEnabled ? ttl : null,
      insertAt:
        scheduleEnabled && scheduledTime
          ? new Date(scheduledTime).getTime()
          : 0,
      playNow,
      attempts: 0,
      forceDownload,
    }));
    onAdd(pendingKlydos);
  };

  const isSaveDisabled =
    selectedKlydoIds.length === 0 ||
    checkedModes.size === 0 ||
    (checkedModes.has("COLLECTIONS") && !collectionTarget) ||
    (checkedModes.has("EXPLORE") && !exploreCategoryTarget) ||
    // Clearing the number field yields Number("") === 0 — don't let a
    // 0-minute TTL through (the input's min=1 only blocks the spinner).
    (ttlEnabled && (!ttl || ttl < 1)) ||
    // insertAt 0 means "insert now" — scheduling with no time picked would
    // send immediately, the opposite of intent.
    (scheduleEnabled && !scheduledTime);

  return (
    <>
      <DSDialog open={open} onClose={onClose} fullWidth maxWidth="sm">
        <DSDialogTitle>Add Pending Klydo</DSDialogTitle>
        <DSDialogContent>
          <DSBox
            sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}
          >
            <DSBox>
              <DSTypography variant="subtitle2" sx={{ mb: 1 }}>
                Klydo
              </DSTypography>
              <DSPrimaryButton onClick={() => setShowKlydoPicker(true)}>
                {selectedKlydoIds.length > 0
                  ? `Selected: ${selectedKlydoIds.length} klydo(s)`
                  : "Select Klydos"}
              </DSPrimaryButton>
              {!draftsAllowed && (
                <DSTypography
                  variant="caption"
                  sx={{ display: "block", mt: 1, color: "text.secondary" }}
                >
                  Only sealed klydos can be sent to a regular clock — drafts are
                  rejected (dev/artist/rater clocks accept any klydo).
                </DSTypography>
              )}
            </DSBox>

            <DSBox>
              <DSTypography variant="subtitle2" sx={{ mb: 1 }}>
                Insert To
              </DSTypography>
              {ALL_MODES.map((mode) => (
                <DSFormControlLabel
                  key={mode}
                  control={
                    <DSSwitch
                      checked={checkedModes.has(mode)}
                      onChange={() => toggleMode(mode)}
                    />
                  }
                  label={mode}
                />
              ))}
            </DSBox>

            {checkedModes.has("COLLECTIONS") && (
              <DSFormControl fullWidth size="small">
                <DSInputLabel>Collection</DSInputLabel>
                <DSSelect
                  value={collectionTarget}
                  label="Collection"
                  onChange={(e) =>
                    setCollectionTarget(e.target.value as string)
                  }
                >
                  {existingCollections.map((col) => (
                    <DSMenuItem key={col.id} value={col.id}>
                      {collectionNameMap.get(col.id) ?? col.id}
                    </DSMenuItem>
                  ))}
                </DSSelect>
              </DSFormControl>
            )}

            {checkedModes.has("EXPLORE") && (
              <DSFormControl fullWidth size="small">
                <DSInputLabel>Explore Category</DSInputLabel>
                <DSSelect
                  value={exploreCategoryTarget}
                  label="Explore Category"
                  onChange={(e) =>
                    setExploreCategoryTarget(e.target.value as string)
                  }
                >
                  {(exploreCategories ?? []).map((cat) => (
                    <DSMenuItem key={cat.id} value={cat.id}>
                      {cat.name || cat.id}
                    </DSMenuItem>
                  ))}
                </DSSelect>
              </DSFormControl>
            )}

            <DSBox>
              <DSFormControlLabel
                control={
                  <DSSwitch
                    checked={ttlEnabled}
                    onChange={(e) => setTtlEnabled(e.target.checked)}
                  />
                }
                label="Time to Live (TTL)"
              />
              {ttlEnabled && (
                <DSTextField
                  type="number"
                  size="small"
                  label="TTL (minutes)"
                  value={ttl}
                  onChange={(e) => setTtl(Number(e.target.value))}
                  inputProps={{ min: 1 }}
                  sx={{ ml: 2, width: 150 }}
                />
              )}
            </DSBox>

            <DSBox>
              <DSFormControlLabel
                control={
                  <DSSwitch
                    checked={scheduleEnabled}
                    onChange={(e) => setScheduleEnabled(e.target.checked)}
                  />
                }
                label="Schedule for later"
              />
              {scheduleEnabled && (
                <input
                  type="datetime-local"
                  value={scheduledTime}
                  onChange={(e) => setScheduledTime(e.target.value)}
                  style={{ marginLeft: 16, padding: "6px 8px" }}
                />
              )}
            </DSBox>

            <DSFormControlLabel
              control={
                <DSSwitch
                  checked={playNow}
                  onChange={(e) => setPlayNow(e.target.checked)}
                />
              }
              label="Play Now (switch to mode and play immediately)"
            />

            <DSFormControlLabel
              control={
                <DSSwitch
                  checked={forceDownload}
                  onChange={(e) => setForceDownload(e.target.checked)}
                />
              }
              label="Force Download (re-download all assets even if cached)"
            />
          </DSBox>
        </DSDialogContent>
        <DSDialogActions>
          {errorMessage && (
            <DSTypography variant="body2" sx={{ color: "error.main", mr: 2 }}>
              {errorMessage}
            </DSTypography>
          )}
          <DSSecondaryButton onClick={onClose}>Cancel</DSSecondaryButton>
          <DSPrimaryButton
            onClick={handleSave}
            disabled={isSaveDisabled || !!isSaving}
          >
            {isSaving ? "Adding..." : "Add"}
          </DSPrimaryButton>
        </DSDialogActions>
      </DSDialog>

      {showKlydoPicker && (
        <KlydosPickDialog
          onClose={() => setShowKlydoPicker(false)}
          selectedKlydos={selectedKlydoIds}
          onPickKlydos={(ids) => {
            setSelectedKlydoIds(ids);
            setShowKlydoPicker(false);
          }}
          // A regular clock can never be served a draft, so don't offer one in
          // the picker. This is presentation only — the caller's validation
          // before the Firestore write is what actually enforces it.
          klydoType={draftsAllowed ? undefined : KlydoType.Sealed}
        />
      )}
    </>
  );
};

export default AddPendingKlydoDialog;
